import { useMemo } from "react";
import { motion } from "framer-motion";
import { BOT_PROFILES } from "@/lib/botProfiles";

const medals = ["🥇", "🥈", "🥉"];

// Map bot id → local avatar
const botAvatarMap = new Map(BOT_PROFILES.map((b) => [b.id, b.avatarUrl]));

interface LeaderboardProps {
  leaderboardData?: { real: any[]; npc: any[] };
  isLoading?: boolean;
}

const Leaderboard = ({ leaderboardData, isLoading = false }: LeaderboardProps) => {
  const players = useMemo(() => {
    if (!leaderboardData) return [];
    const real = (leaderboardData.real || []).map((p: any) => ({
      user_id: p.user_id,
      display_name: p.display_name,
      username: p.username,
      avatar_url: p.avatar_url,
      level: p.level,
      xp: p.xp,
      total_wins: p.total_wins,
      total_games: p.total_games,
      rank_points: p.rank_points,
    }));
    const npcs = (leaderboardData.npc || []).map((n: any) => ({
      user_id: n.id,
      display_name: n.display_name,
      username: n.username,
      avatar_url: botAvatarMap.get(n.id) || null,
      level: n.level,
      xp: n.xp,
      total_wins: n.total_wins,
      total_games: n.total_games,
      rank_points: n.rank_points,
    }));
    return [...real, ...npcs];
  }, [leaderboardData]);

  const topPlayers = useMemo(() => {
    const merged = [...players];
    merged.sort((a, b) => (b.rank_points ?? 0) - (a.rank_points ?? 0));
    return merged.slice(0, 5);
  }, [players]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-pulse text-white/60 font-body">Φόρτωση κατάταξης...</div>
      </div>
    );
  }

  if (!topPlayers || topPlayers.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-white/60 font-body">Γίνε ο πρώτος στην κατάταξη! 🏆</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {topPlayers.map((player, i) => (
        <motion.div
          key={player.user_id + i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className={`flex items-center gap-4 p-4 rounded-2xl border ${
            i === 0 ? "bg-black/80 border-amber-300/40" : "bg-black/70 border-white/10"
          }`}
        >
          <div className="text-2xl w-8 text-center font-display text-white">
            {i < 3 ? medals[i] : `${i + 1}.`}
          </div>

          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-2 border-white/20">
            {player.avatar_url ? (
              <img src={player.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg">🎮</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-white truncate">
              {player.display_name || player.username || "Ανώνυμος"}
            </p>
            <p className="text-xs text-white/50 font-body">
              Επίπεδο {player.level} · {player.total_wins} νίκες
            </p>
          </div>

          <div className="text-right">
            <p className="font-display font-bold text-amber-300">{player.rank_points ?? 0}</p>
            <p className="text-xs text-white/50 font-body">RP</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default Leaderboard;
